import { useCallback, useEffect, useRef, useState } from "react";
import { cn } from "../lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./tooltip";

type TooltipContentProps<T extends React.ReactNode> = {
  children?: T;
  content?: string;
  maxLines?: number;
  onClick?: () => void;
  className?: string;
};
const TruncatedText = <T extends React.ReactNode>({
  children,
  content,
  maxLines = 2,
  onClick,
  className,
}: TooltipContentProps<T>) => {
  const spanRef = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  const checkTruncation = useCallback(() => {
    const el = spanRef.current;
    if (!el) return;
    const style = getComputedStyle(el);
    const lineHeight = Number.parseFloat(style.lineHeight);
    const maxHeight = maxLines * lineHeight;

    // Check overflow for multi-line
    const truncatedByHeight = el.scrollHeight > maxHeight + 1;

    // Check overflow for single line
    const originalWhiteSpace = el.style.whiteSpace;
    el.style.whiteSpace = "nowrap";
    const truncatedByWidth = el.scrollWidth > el.clientWidth;
    el.style.whiteSpace = originalWhiteSpace;
    setIsTruncated(truncatedByHeight || truncatedByWidth);
  }, [maxLines]);

  useEffect(() => {
    checkTruncation();
  }, [checkTruncation]);

  useEffect(() => {
    const el = spanRef.current;
    if (!el) return;

    const observer = new window.ResizeObserver(checkTruncation);
    observer.observe(el);
    window.addEventListener("resize", checkTruncation);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", checkTruncation);
    };
  }, [checkTruncation]);

  if (children == null || children === "") {
    return null;
  }

  const span = (
    <span
      ref={spanRef}
      className={cn("w-full overflow-hidden font-normal", className)}
      style={{
        WebkitLineClamp: maxLines,
        display: "-webkit-box",
        WebkitBoxOrient: "vertical",
      }}
      onClick={onClick}
      onKeyUp={(e) => {
        if (onClick && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          onClick();
        }
      }}
    >
      {children}
    </span>
  );

  if (!isTruncated) {
    return span;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>{span}</TooltipTrigger>
      <TooltipContent>{content ?? children}</TooltipContent>
    </Tooltip>
  );
};
export {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
  TruncatedText,
};
